import { Observable, zip } from "rxjs"
import { switchMap, filter } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import {
    createActionResetGlobalFilter,
    SET_FILTER_VALUE,
    SET_FILTERS,
    setGlobalDefaultFilter,
} from "./actions"
import { FETCH_SETTINGS_SUCCESS } from "../../api/actions"
import { ACCOUNT_SELECT_CLICKED, LOAD_AUTH_LOCATIONS_SUCCESS } from "../layouts/header/actions"
import { getMidnightOffset } from "../screens/settings/selectors"
import { cookieNames, getCookie, setCookie } from "../../utils/cookies"
import type { EpicDependencies } from "../rxjs/configureEpics"
import { getLocationsAuthData } from "../auth/selectors"
import { Router } from "../../server/router"
import { getDateString, getToday, getUtcEndOfDay, getUtcStartOfDay } from "../../utils/dateFunctions"
import { Action, StateObject } from "../redux/types"
import { isOrderReportAgendaOpened } from "../screens/reports/orders/selectors"

export const agendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    zip(actions$.pipe(ofType(LOAD_AUTH_LOCATIONS_SUCCESS)), actions$.pipe(ofType(FETCH_SETTINGS_SUCCESS))).pipe(
        switchMap(() => {
            const today = getToday(deps.getState())
            const midnightOffset = getMidnightOffset(deps.getState())
            const locationFilterIdsFromCookies = getCookie(cookieNames.locationFilterIds)
            const locationFilterIdsCookiesParsed = locationFilterIdsFromCookies
                ? JSON.parse(locationFilterIdsFromCookies)
                : null
            const serverLocations = getLocationsAuthData(deps.getState())
            const serverLocationIds = serverLocations.map(l => l.id)
            let finalLocationIds = []

            // No location in cookies yet, save
            if (!locationFilterIdsCookiesParsed) {
                setCookie(cookieNames.locationFilterIds, JSON.stringify(serverLocationIds))
                // @ts-expect-error
                finalLocationIds = serverLocationIds
            } // Location found in cookies, and account has these locations
            else if (locationFilterIdsCookiesParsed.filter(cl => serverLocationIds.indexOf(cl) !== -1).length !== 0) {
                finalLocationIds = locationFilterIdsCookiesParsed
            } // Location found in cookies, but account has no such locations
            else {
                // @ts-expect-error
                finalLocationIds = serverLocationIds
            }

            return [
                setGlobalDefaultFilter(
                    finalLocationIds,
                    getUtcStartOfDay(today, null, midnightOffset),
                    getUtcEndOfDay(today, null, midnightOffset),
                    getDateString(today),
                    getDateString(today)
                ),
            ]
        })
    )

export const resetGlobalFilterLocationsEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECT_CLICKED),
        switchMap(() => {
            return [createActionResetGlobalFilter()]
        })
    )

export const setKnownFilterToUrlEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_FILTER_VALUE, SET_FILTERS),
        // Only for Orders Report so far
        filter(() => isOrderReportAgendaOpened(deps.getState())),
        switchMap((action: Action) => {
            // date range filter
            const dateFilterValue =
                action.type === SET_FILTERS
                    ? action.payload.filters.date
                    : action.payload.type === "date" && action.payload.filterValue
            if (action.payload.identifier === "global" && dateFilterValue) {
                const { from, to } = dateFilterValue
                Router.push({
                    pathname: Router.asPath.split("?")[0],
                    query: { ...Router.query, from: from, to: to },
                })
            }

            // search text filter
            if (action.payload.identifier === "orders" && action.payload.type === "search") {
                const searchText = action.payload.filterValue?.value
                const { searchText: prevQuerySearchText, ...queryWithoutSearchText } = Router.query

                if (prevQuerySearchText !== searchText) {
                    Router.push({
                        pathname: Router.asPath.split("?")[0],
                        query: {
                            ...queryWithoutSearchText,
                            ...(searchText === "" ? {} : { searchText: searchText }),
                        },
                    })
                }
            }

            return []
        })
    )

export default [
    agendaMountedEpics,
    resetGlobalFilterLocationsEpics,
    setKnownFilterToUrlEpic,
]
